import React, { useEffect, useMemo, useRef } from "react"
import { useLocation, useNavigate, useParams, type Location } from "react-router-dom"

export type { Location }

/**
 * Слой совместимости с react-router 5: `withRouter`, `useHistory` и объект
 * `history` с методами `push` / `replace` / `goBack` / `listen`.
 *
 * В шестой версии всего этого нет — есть хуки `useNavigate` и `useLocation`.
 * Перевести на них два с половиной десятка классовых компонентов и ещё столько
 * же подписок на смену адреса — отдельная работа, не связанная с апгрейдом
 * самого роутера. Поэтому здесь собран тот же набор поверх новых хуков, а
 * страницы переводятся на хуки по мере того, как их и так правят.
 *
 * Новый код пишем сразу на `useNavigate` / `useLocation` / `useParams`.
 */
export type UnregisterCallback = () => void

export interface History {
    push: (path: string) => void
    replace: (path: string) => void
    goBack: () => void
    location: Location
    listen: (callback: (location: Location) => void) => UnregisterCallback
}

/** @deprecated используйте `useNavigate` и `useLocation` */
export type RouterHistory = History

export interface RouteComponentProps<TParams = Record<string, string | undefined>> {
    history: History
    location: Location
    match: {
        params: TParams
        path: string
        url: string
        isExact: boolean
    }
}

/** @deprecated имя из react-router 5; то же самое, что RouteComponentProps */
export type RouteChildrenProps<TParams = Record<string, string | undefined>> = RouteComponentProps<TParams>

/**
 * Подписчики на смену адреса. В пятой версии их держал сам объект history,
 * в шестой такого объекта наружу нет — поэтому список свой, а рассылает
 * уведомления `LocationBroadcaster`, воткнутый в каркас приложения.
 */
const listeners = new Set<(location: Location) => void>()

const listen = (callback: (location: Location) => void): UnregisterCallback => {
    listeners.add(callback)

    return () => {
        listeners.delete(callback)
    }
}

/**
 * Рассылает смену адреса подписчикам `history.listen`. Ставится один раз, в каркасе.
 *
 * Первый адрес пропускается: в пятой версии подписка при регистрации не срабатывала,
 * колбэк приходил только на переход. Есть и разница, которую воспроизвести нечем —
 * там колбэк вызывался до перерисовки, здесь после коммита; подписчикам это заметно
 * только одним лишним промежуточным рендером.
 */
export const LocationBroadcaster = () => {
    const location = useLocation()
    const known = useRef<Location | null>(null)

    useEffect(() => {
        const first = known.current === null
        known.current = location

        if (!first) {
            listeners.forEach((callback) => callback(location))
        }
    }, [location])

    return null
}

/**
 * Объект `history` живёт всё время жизни компонента и не меняется по ссылке.
 *
 * Это не косметика: в пятой версии `useHistory()` отдавал синглтон, и код на это
 * опирается — `history` стоит в списке зависимостей `useEffect` на полудюжине
 * страниц. Новый объект на каждом рендере означал бы перезапуск эффекта, то есть
 * повторную загрузку списка и карточки на каждый чих, а заодно ломал бы отсечение
 * рендера у `PureComponent`. Актуальные `navigate` и `location` берутся из ref-ов.
 */
export function useHistory(): History {
    const navigate = useNavigate()
    const location = useLocation()

    const navigateRef = useRef(navigate)
    const locationRef = useRef(location)
    navigateRef.current = navigate
    locationRef.current = location

    const history = useRef<History>()

    if (!history.current) {
        history.current = {
            push: (path: string) => navigateRef.current(path),
            replace: (path: string) => navigateRef.current(path, { replace: true }),
            goBack: () => navigateRef.current(-1),
            get location() {
                return locationRef.current
            },
            listen,
        }
    }

    return history.current
}

export function useRouterProps<TParams = Record<string, string | undefined>>(): RouteComponentProps<TParams> {
    const history = useHistory()
    const location = useLocation()
    const params = useParams()

    const paramsRef = useRef(params)
    paramsRef.current = params

    // объект пропов тоже должен быть стабильным между рендерами, иначе обёрнутые
    // PureComponent-страницы перерисовываются всегда. Параметры выводятся из адреса,
    // поэтому зависимости достаточно от него
    return useMemo(
        () => ({
            history,
            location,
            match: {
                params: paramsRef.current as unknown as TParams,
                // шаблон маршрута в v6 наружу не отдаётся; код проекта смотрит только
                // на params, остальное оставлено ради совместимости типов
                path: location.pathname,
                url: location.pathname,
                isExact: true,
            },
        }),
        [history, location]
    )
}

export function withRouter<TProps extends object>(
    Component: React.ComponentType<TProps>
): React.FC<Omit<TProps, keyof RouteComponentProps>> {
    const Wrapped: React.FC<Omit<TProps, keyof RouteComponentProps>> = (props) => {
        const routerProps = useRouterProps()

        // приведение к any — единственное место, где оно нужно: у обёрнутых
        // компонентов параметры маршрута типизированы по-своему
        return <Component {...({ ...props, ...routerProps } as any)} />
    }

    Wrapped.displayName = `withRouter(${Component.displayName || Component.name || "Component"})`

    return Wrapped
}
